import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Download } from 'lucide-react'
import { toast } from 'sonner'
import {
  apiError,
  isNotFound,
  useMeQuery,
  usePriceListQuery,
  useRecordTransactionMutation,
  useRetryGrantMutation,
  useTransactionsQuery,
} from '@/store/api'
import type { Transaction, TransactionType } from '@shared/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input, Label, FieldError } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { PeriodSelect, recentPeriods } from '@/components/ui/period-select'
import { Table, THead, Th, Tr, Td, TotalsRow, SkeletonRows } from '@/components/ui/data-table'
import { EmptyState, ErrorState } from '@/components/ui/empty-state'
import { Term } from '@/components/ui/term'
import { Tooltip } from '@/components/ui/tooltip'
import { NotFound } from '@/pages/NotFound'
import { downloadCsv } from '@/lib/csv'
import { fmtDate, fmtMoney, fmtPeriod } from '@/lib/format'

export const TYPE_LABELS: Record<TransactionType, string> = {
  new_sale: 'New sale',
  renewal: 'Renewal',
  credit_pack: 'Credit pack',
  pro_upgrade: 'Pro upgrade',
  smb_budget: 'SMB budget',
  refund: 'Refund',
  cancellation: 'Cancellation',
}

const GRANT_BADGES = {
  granted: { variant: 'active', label: 'Applied' },
  not_needed: { variant: 'neutral', label: 'On activation' },
  pending: { variant: 'pending', label: 'Pending' },
  failed: { variant: 'revoked', label: 'Failed' },
} as const

/**
 * One row per paid transaction the host reported, for one Billing Period. Shows Atarim's
 * list price at purchase and the Expansion Charge — never what the partner charged.
 */
export function Charges() {
  const { slug = '' } = useParams()
  const { data: me } = useMeQuery()
  const isAdmin = me?.role === 'atarim_admin'
  const [period, setPeriod] = useState(() => recentPeriods()[0])
  const [recordOpen, setRecordOpen] = useState(false)
  const { data, isLoading, isError, error, refetch } = useTransactionsQuery({ slug, period })

  if (isError && isNotFound(error)) return <NotFound />

  const exportCsv = () => {
    if (!data) return
    const money = (minor: number) => (minor / 100).toFixed(2)
    downloadCsv(
      `atarim-charges-${slug}-${period}.csv`,
      ['Date (UTC)', 'Customer', 'Type', 'Reference', `List price (${data.currency})`, `Expansion Charge (${data.currency})`, 'Relates to'],
      [
        ...data.rows.map((r) => [
          r.occurredAt.slice(0, 10),
          r.customer?.label ?? '',
          TYPE_LABELS[r.type],
          r.externalRef,
          money(r.listPriceMinor),
          money(r.chargeMinor),
          r.late ? r.occurredAt.slice(0, 7) : '',
        ]),
        ['Total', '', '', '', '', money(data.totalChargeMinor), ''],
      ],
    )
  }

  const cols = isAdmin ? 7 : 6

  return (
    <>
      <PageHeader
        title="Charges"
        description={
          <>
            Every paid transaction reported for this brand. Each <Term id="expansion-charge">Expansion Charge</Term> is 50% of
            Atarim's <Term id="list-price">list price</Term> at the time of purchase, billed in arrears.
          </>
        }
        actions={
          <>
            <PeriodSelect value={period} onChange={setPeriod} />
            <Button variant="secondary" onClick={exportCsv} disabled={!data?.rows.length}>
              <Download className="size-4" /> Export CSV
            </Button>
            {isAdmin && <Button onClick={() => setRecordOpen(true)}>Record transaction</Button>}
          </>
        }
      />

      {data && (
        <p className="mb-4 text-sm text-muted-foreground">
          {fmtPeriod(period)}:{' '}
          <span className="font-medium text-foreground">{fmtMoney(data.totalChargeMinor, data.currency)}</span>{' '}
          {data.closed ? 'final — this period is closed.' : 'provisional until the period is closed.'}
        </p>
      )}

      {isError ? (
        <ErrorState onRetry={refetch} />
      ) : !isLoading && data?.rows.length === 0 ? (
        <EmptyState title={`No transactions in ${fmtPeriod(period)}`} message="Transactions appear here as your billing system reports them." />
      ) : (
        <Table>
          <THead>
            <Th>Date</Th>
            <Th>Customer</Th>
            <Th>Type</Th>
            <Th>Reference</Th>
            <Th right>List price</Th>
            <Th right>Expansion Charge</Th>
            {isAdmin && <Th>Applied on platform</Th>}
          </THead>
          {isLoading ? (
            <SkeletonRows cols={cols} />
          ) : (
            <tbody>
              {data!.rows.map((r) => (
                <ChargeRow key={r.id} row={r} slug={slug} isAdmin={isAdmin} />
              ))}
              <TotalsRow>
                <Td colSpan={5}>Total</Td>
                <Td right>{fmtMoney(data!.totalChargeMinor, data!.currency)}</Td>
                {isAdmin && <Td />}
              </TotalsRow>
            </tbody>
          )}
        </Table>
      )}

      {isAdmin && <RecordTransactionDialog slug={slug} open={recordOpen} onOpenChange={setRecordOpen} />}
    </>
  )
}

function ChargeRow({ row: r, slug, isAdmin }: { row: Transaction; slug: string; isAdmin: boolean }) {
  const [retry, { isLoading }] = useRetryGrantMutation()
  return (
    <Tr>
      <Td className="whitespace-nowrap">
        {fmtDate(r.occurredAt)}
        {r.late && (
          <Tooltip content={`Reported after ${fmtPeriod(r.occurredAt.slice(0, 7))} was closed, so it is charged in this period.`}>
            <span className="ml-1.5">
              <Badge variant="expiring">Late</Badge>
            </span>
          </Tooltip>
        )}
      </Td>
      <Td className="max-w-[18rem] truncate">{r.customer?.label ?? '—'}</Td>
      <Td>
        {TYPE_LABELS[r.type]}
        {r.reversesExternalRef && <span className="block text-xs text-muted-foreground">of {r.reversesExternalRef}</span>}
      </Td>
      <Td className="font-mono text-xs">{r.externalRef}</Td>
      <Td right>{fmtMoney(r.listPriceMinor, r.currency)}</Td>
      <Td right className={r.chargeMinor < 0 ? 'text-ata-green' : ''}>
        {fmtMoney(r.chargeMinor, r.currency)}
      </Td>
      {isAdmin && r.grantStatus && (
        <Td>
          <span className="inline-flex items-center gap-2">
            <Tooltip content={r.grantError ?? GRANT_BADGES[r.grantStatus].label}>
              <span>
                <Badge variant={GRANT_BADGES[r.grantStatus].variant}>{GRANT_BADGES[r.grantStatus].label}</Badge>
              </span>
            </Tooltip>
            {(r.grantStatus === 'failed' || r.grantStatus === 'pending') && (
              <Button
                variant="ghost"
                size="sm"
                loading={isLoading}
                onClick={async () => {
                  try {
                    const after = await retry({ slug, id: r.id }).unwrap()
                    toast[after.grantStatus === 'granted' ? 'success' : 'error'](
                      after.grantStatus === 'granted' ? 'Applied on the platform' : (after.grantError ?? 'Still not applied'),
                    )
                  } catch (err) {
                    toast.error(apiError(err))
                  }
                }}
              >
                Retry
              </Button>
            )}
          </span>
        </Td>
      )}
    </Tr>
  )
}

const TYPE_OPTIONS = (Object.keys(TYPE_LABELS) as TransactionType[]).map((t) => ({ value: t, label: TYPE_LABELS[t] }))

/** Admin: record a transaction reported outside the API, under the same rules. */
function RecordTransactionDialog({ slug, open, onOpenChange }: { slug: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { data: prices } = usePriceListQuery(slug, { skip: !open })
  const [record, { isLoading }] = useRecordTransactionMutation()
  const [type, setType] = useState<TransactionType>('credit_pack')
  const [externalRef, setExternalRef] = useState('')
  const [target, setTarget] = useState('')
  const [sku, setSku] = useState('')
  const [credits, setCredits] = useState('')
  const [renews, setRenews] = useState<'pro' | 'smb'>('pro')
  const [occurredOn, setOccurredOn] = useState('')
  const [reversesRef, setReversesRef] = useState('')
  const [error, setError] = useState<string | null>(null)
  const reversing = type === 'refund' || type === 'cancellation'
  // SMB budgets are priced on the brand's credit scale, not a price-list sku.
  const forSmb = type === 'smb_budget' || (type === 'renewal' && renews === 'smb')
  // A Website Owner plan is sold to an SMB owner, so it names one of their sites.
  const forOwner = forSmb || prices?.items.find((i) => i.sku === sku)?.kind === 'website_owner'

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await record({
        slug,
        input: {
          externalRef,
          type,
          ...(reversing
            ? { reversesRef }
            : forSmb
              ? { credits: parseInt(credits, 10), smbSiteId: target.trim() }
              : forOwner
                ? { sku, smbSiteId: target.trim() }
                : { sku, licenseId: target.trim() }),
          ...(occurredOn ? { occurredAt: `${occurredOn}T12:00:00.000Z` } : {}),
        },
      }).unwrap()
      toast.success('Transaction recorded')
      onOpenChange(false)
      setExternalRef('')
      setTarget('')
      setReversesRef('')
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Record transaction"
        description="For a sale reported outside the API. The list price comes from the brand's price list, or its credit scale for SMB budgets; resending a reference returns the original."
      >
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select ariaLabel="Type" value={type} onValueChange={(v) => setType(v as TransactionType)} options={TYPE_OPTIONS} className="w-full" />
            </div>
            <div>
              <Label htmlFor="tx-ref">Reference</Label>
              <Input id="tx-ref" required placeholder="Invoice or order id" value={externalRef} onChange={(e) => setExternalRef(e.target.value)} />
            </div>
          </div>
          {reversing ? (
            <div>
              <Label htmlFor="tx-reverses">Reference of the sale being {type === 'refund' ? 'refunded' : 'cancelled'}</Label>
              <Input id="tx-reverses" required value={reversesRef} onChange={(e) => setReversesRef(e.target.value)} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {type === 'renewal' && (
                <div className="col-span-2">
                  <Label>Renewal of</Label>
                  <Select
                    ariaLabel="Renewal of"
                    value={renews}
                    onValueChange={(v) => setRenews(v as 'pro' | 'smb')}
                    options={[
                      { value: 'pro', label: 'Pro or Website Owner plan' },
                      { value: 'smb', label: 'SMB monthly budget' },
                    ]}
                    className="w-full"
                  />
                </div>
              )}
              {forSmb ? (
                <div>
                  <Label htmlFor="tx-credits">Monthly credits (multiple of 1,000)</Label>
                  <Input id="tx-credits" required type="number" min={1000} step={1000} value={credits} onChange={(e) => setCredits(e.target.value)} />
                </div>
              ) : (
                <div>
                  <Label>Product</Label>
                  <Select
                    ariaLabel="Product"
                    value={sku || '__none'}
                    onValueChange={(v) => setSku(v === '__none' ? '' : v)}
                    options={[
                      { value: '__none', label: 'Choose…' },
                      ...(prices?.items ?? []).map((i) => ({ value: i.sku, label: `${i.name} (${fmtMoney(i.amountMinor, prices!.currency)})` })),
                    ]}
                    className="w-full"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="tx-target">{forOwner ? 'SMB site id (charged to its owner)' : 'Licence id'}</Label>
                <Input id="tx-target" required value={target} onChange={(e) => setTarget(e.target.value)} />
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="tx-date">Date of sale (UTC, optional — defaults to now)</Label>
            <Input id="tx-date" type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} />
          </div>
          <FieldError>{error}</FieldError>
          <Button type="submit" className="w-full" loading={isLoading}>
            Record transaction
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
